/**
 * PrizeSymbol — a prize rendered as a square symbol: its image when present,
 * otherwise a branded disc with the prize's monogram. Lets reel/card games
 * show prizes WITHOUT the ugly "Lime …" / "Brand…" truncation from the audit.
 */
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { WheelItem } from '../pickPrize';
import { mix, readableText, shade, tint } from './colors';

/** A distinct, brand-derived fill for the symbol at `index`. */
export function symbolColor(index: number, primary: string, accent: string): string {
  const stops = [accent, mix(accent, primary, 0.5), primary, tint(primary, 0.25), shade(accent, 0.7)];
  return stops[index % stops.length];
}

interface PrizeSymbolProps {
  item: WheelItem;
  size: number;
  index: number;
  primary: string;
  accent: string;
}

export function PrizeSymbol({ item, size, index, primary, accent }: PrizeSymbolProps) {
  const image = item.logo ?? item.image ?? null;
  if (image) {
    return (
      <img
        src={resolveImageUrl(image)}
        alt=""
        className="object-contain"
        style={{ width: size, height: size }}
      />
    );
  }
  const bg = symbolColor(index, primary, accent);
  const letter = item.name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className="grid place-items-center rounded-full font-black"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 35% 30%, ${tint(bg, 0.3)}, ${shade(bg, 0.7)})`,
        color: readableText(bg),
        fontSize: size * 0.46,
        boxShadow: `inset 0 1px 2px rgba(255,255,255,0.4)`,
      }}
    >
      {letter}
    </div>
  );
}
